import type { User } from "@supabase/supabase-js";

function identityProviders(user: User | null | undefined): string[] {
  const ids = user?.identities;
  if (!Array.isArray(ids)) return [];
  return ids.map((row) => String(row?.provider || "").toLowerCase()).filter(Boolean);
}

/** Magic link / email+password flows use the `email` identity provider in Supabase. */
export function hasEmailAuthIdentity(user: User | null | undefined): boolean {
  return identityProviders(user).includes("email");
}

/** Google OAuth adds a `google` identity; those users should not see the email-only password prompt. */
export function hasGoogleAuthIdentity(user: User | null | undefined): boolean {
  return identityProviders(user).includes("google");
}

/** User finished this prompt (password saved) or chose to skip. */
export function hasCompletedPasswordSetupPrompt(user: User | null | undefined): boolean {
  const m = user?.user_metadata as Record<string, unknown> | undefined;
  return m?.password_setup_complete === true || m?.password_setup_skipped === true;
}

/**
 * Offer “set a password” only for email-based sign-in (magic link / invite) without Google linked.
 * Supabase often still lists an `email` identity for Google users, so we require absence of `google`.
 */
export function shouldOfferFirstTimePasswordSetup(user: User | null | undefined): boolean {
  if (!user) return false;
  if (hasCompletedPasswordSetupPrompt(user)) return false;
  if (hasGoogleAuthIdentity(user)) return false;
  return hasEmailAuthIdentity(user);
}
